import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Lock, Loader2, Eye, EyeOff, CheckCircle, ArrowLeft } from "lucide-react";
import axios from "axios";
import SiteLogo from "@/components/SiteLogo";
import { formatCopyright } from "@/lib/brandConfig";
import { getSiteBrand } from "@/lib/siteHosts";

// Email validation helper function
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // State for request reset form
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  
  // State for reset password form
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordTouched, setPasswordTouched] = useState({});
  
  // Common states
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Determine which view to show based on token presence
  const isResetMode = !!token;

  // Handle email change for request form
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setApiError("");
    
    if (emailTouched) {
      if (!value) {
        setEmailError("Email is required");
      } else if (!validateEmail(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    if (!email) {
      setEmailError("Email is required");
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  // Handle password changes for reset form
  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    setApiError("");
    
    if (passwordTouched.newPassword) {
      validatePasswords(value, confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setApiError("");
    
    if (passwordTouched.confirmPassword) {
      validatePasswords(newPassword, value);
    }
  };

  const validatePasswords = (newPass, confirmPass) => {
    const errors = {};
    
    if (!newPass) {
      errors.newPassword = "Password is required";
    } else if (newPass.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    
    if (!confirmPass) {
      errors.confirmPassword = "Please confirm your password";
    } else if (newPass !== confirmPass) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordBlur = (field) => {
    setPasswordTouched((prev) => ({ ...prev, [field]: true }));
    validatePasswords(newPassword, confirmPassword);
  };

  // Submit request reset form
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setEmailTouched(true);
    
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setApiError("");

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/password-reset-request`, {
        email: email,
      });

      const data = response.data;

      if (data.status === "ok") {
        setSuccess(true);
        setSuccessMessage("If an account with that email exists, you'll receive a password reset link shortly.");
      } else {
        setApiError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Password reset request error:", err);
      
      if (err.response) {
        const errorMessage = err.response.data?.detail || err.response.data?.message || "Something went wrong. Please try again.";
        setApiError(errorMessage);
      } else if (err.request) {
        setApiError("Network error. Please check your connection and try again.");
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Submit reset password form
  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setPasswordTouched({ newPassword: true, confirmPassword: true });
    
    if (!validatePasswords(newPassword, confirmPassword)) {
      return;
    }

    setIsLoading(true);
    setApiError("");

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/password-reset`, {
        token: token,
        new_password: newPassword,
      });

      const data = response.data;

      if (data.status === "ok") {
        setSuccess(true);
        setSuccessMessage("Your password has been reset successfully. You can now sign in with your new password.");
      } else {
        setApiError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Password reset error:", err);
      
      if (err.response) {
        const errorMessage = err.response.data?.detail || err.response.data?.message || "Something went wrong. Please try again.";
        setApiError(errorMessage);
      } else if (err.request) {
        setApiError("Network error. Please check your connection and try again.");
      } else {
        setApiError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isEmailValid = email && validateEmail(email);
  const isPasswordValid = newPassword && newPassword.length >= 6 && confirmPassword && newPassword === confirmPassword;

  // Success View
  if (success) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex flex-col" data-testid="password-reset-page">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
            <Link to="/">
              <SiteLogo className="h-6 md:h-7 w-auto" />
            </Link>
          </div>
        </header>

        {/* Success Content */}
        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-10 radial-gradient-hero">
          <div className="w-full max-w-md animate-fade-in-up">
            <div className="glass rounded-2xl p-8 md:p-10 border border-white/10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#00D1FF]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#00D1FF]" />
              </div>
              
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-4">
                {isResetMode ? "Password Reset Complete" : "Check Your Email"}
              </h1>
              
              <p className="text-[#9CA3AF] font-body text-sm mb-8">
                {successMessage}
              </p>
              
              {isResetMode ? (
                <Link
                  to="/login"
                  className="btn-pulse inline-flex items-center gap-2 bg-[#00D1FF] text-[#0F1115] font-heading font-bold py-3 px-6 rounded-lg hover:bg-[#00D1FF]/90 transition-colors"
                  data-testid="go-to-login-button"
                >
                  Sign In
                  <ArrowRight className="w-5 h-5" />
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="text-[#00D1FF] font-body text-sm hover:text-[#00D1FF]/80 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Sign In
                </Link>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex flex-col" data-testid="password-reset-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0F1115]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/">
              <SiteLogo className="h-6 md:h-7 w-auto" testId="reset-logo" />
          </Link>
          <Link 
            to="/login"
            className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors font-body flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-10 radial-gradient-hero">
        <div className="w-full max-w-md animate-fade-in-up">
          {/* Card */}
          <div className="glass rounded-2xl p-8 md:p-10 border border-white/10">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">
                {isResetMode ? "Create New Password" : "Reset Your Password"}
              </h1>
              <p className="text-[#9CA3AF] font-body text-sm">
                {isResetMode 
                  ? "Enter your new password below"
                  : "Enter your email and we'll send you a reset link"
                }
              </p>
            </div>

            {/* API Error */}
            {apiError && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="api-error">
                <p className="text-red-400 text-sm font-body text-center">{apiError}</p>
              </div>
            )}

            {/* Request Reset Form */}
            {!isResetMode && (
              <form onSubmit={handleRequestSubmit} className="space-y-5">
                {/* Email Input */}
                <div className="space-y-2">
                  <label htmlFor="email-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                    <Mail className="w-4 h-4 text-[#00D1FF]" />
                    Email Address
                  </label>
                  <input
                    id="email-input"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder="your@email.com"
                    disabled={isLoading}
                    className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                      emailError && emailTouched
                        ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                        : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                    }`}
                    data-testid="email-input"
                  />
                  {emailError && emailTouched && (
                    <p className="text-red-400 text-xs font-body">{emailError}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isEmailValid || isLoading}
                  className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                    isEmailValid && !isLoading
                      ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                      : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                  }`}
                  data-testid="request-reset-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Reset Password Form */}
            {isResetMode && (
              <form onSubmit={handleResetSubmit} className="space-y-5">
                {/* New Password Input */}
                <div className="space-y-2">
                  <label htmlFor="new-password-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                    <Lock className="w-4 h-4 text-[#00D1FF]" />
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password-input"
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      onBlur={() => handlePasswordBlur("newPassword")}
                      placeholder="Enter new password (min. 6 characters)"
                      disabled={isLoading}
                      className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                        passwordErrors.newPassword && passwordTouched.newPassword
                          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                      }`}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && passwordTouched.newPassword && (
                    <p className="text-red-400 text-xs font-body">{passwordErrors.newPassword}</p>
                  )}
                </div>

                {/* Confirm Password Input */}
                <div className="space-y-2">
                  <label htmlFor="confirm-password-input" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
                    <Lock className="w-4 h-4 text-[#00D1FF]" />
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password-input"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      onBlur={() => handlePasswordBlur("confirmPassword")}
                      placeholder="Confirm your new password"
                      disabled={isLoading}
                      className={`w-full bg-white/5 border rounded-lg px-4 py-3 pr-12 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body disabled:opacity-50 ${
                        passwordErrors.confirmPassword && passwordTouched.confirmPassword
                          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                      }`}
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && passwordTouched.confirmPassword && (
                    <p className="text-red-400 text-xs font-body">{passwordErrors.confirmPassword}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!isPasswordValid || isLoading}
                  className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                    isPasswordValid && !isLoading
                      ? "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                      : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                  }`}
                  data-testid="reset-password-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      Reset Password
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Back to Login Link */}
            <div className="mt-8 text-center">
              <Link
                to="/login"
                className="text-[#9CA3AF] text-sm font-body hover:text-[#F3F4F6] transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <Link
              to="/privacy-policy"
              className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-[#9CA3AF]/30">|</span>
            <Link
              to="/terms"
              className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
            >
              Terms of Service
            </Link>
          </div>
          <p className="text-[#9CA3AF]/60 text-xs font-mono">
            {`${formatCopyright(getSiteBrand())}. All rights reserved.`}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PasswordReset;
